import axios, {AxiosInstance, AxiosRequestConfig, Method} from "axios";
import {useSystemStore} from "@/stores/system/SystemStore";
import {HttpType, UsersType} from "@/utilities/system/enums/Enums";
import {GeneralResponse} from "@/models/system/api/Responses";
import {TokenService} from "@/utilities/system/services/TokenService";
import {baseURL} from "@/AppConfig";
import {PathHelper} from "@/utilities/system/helpers/PathHelper";


export type HttpClientResponse<T> = [null, T] | [Error, any]

export class WebCallService {
    private axiosInstance : AxiosInstance;
    private url: string;
    private readonly baseUrl: string;
    private tokenService:TokenService;
    private pathHelper:PathHelper;

    constructor(){
        this.url = '';
        this.baseUrl = baseURL+'/api';
        this.axiosInstance = axios.create({
            withCredentials: true,
            baseURL: this.baseUrl
        });
        this.tokenService = new TokenService();
        this.pathHelper = new PathHelper();
    }

    public async HttpSend(type:HttpType, uri:string, body:any = null):Promise<any>{
        // await this.tokenService.EnsureFreshToken(uri, type);

        let response;

        switch(type){
            case HttpType.post: {
                response = await this.sendPostRequest(uri, body);
                break;
            }
            case HttpType.get: {
                response = await this.sendGetRequest(uri);
                break;
            }
            case HttpType.put: {
                response = await this.sendPutRequest(uri, body);
                break;
            }
            case HttpType.delete: {
                response = await this.sendDeleteRequest(uri, body);
                break;
            }
        }

        return this.convertErrorIfNeeded(response);
    }

    public async sendGetRequest<T = unknown>(path: string): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("get", path, null));
    }

    public async sendPostRequest<T = unknown>(path: string, bodyContent: any): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("post", path, bodyContent));
    }

    public async sendPutRequest<T = unknown>(path: string, bodyContent: any): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("put", path, bodyContent));
    }

    public async sendDeleteRequest<T = unknown>(path: string, bodyContent: any): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("delete", path, bodyContent));
    }

    /*
    *   creates new axios instance and cleanly gets from an external API without response conversion or token workflow
    */
    public async GetFromExternalAPI(uri:string, withCredentials:boolean = false):Promise<any>{
        return withCredentials
            ? await axios.create().get(uri, {withCredentials:true})
            : await axios.create().get(uri);
    }


    /*--- PRIVATE ---*/


    private async axiosCall<T = unknown>(config: AxiosRequestConfig): Promise<HttpClientResponse<T>>
    {

        try {
            const req = await this.axiosInstance.request<T>(config);
            return [null, req.data];
        }
        catch (error:any) {
            return [new Error(error as string), error.response]; //return response as data to read HttpCode
        }
    }

    private async getRequestConfig(requestMethod: Method, path: string, bodyContent: any) : Promise<AxiosRequestConfig>
    {
        const isStartupRequest = path === this.pathHelper.misc.getStartupConfigPath();
        this.url = `${this.baseUrl}/${path}`;

        const systemStore = useSystemStore();

        const token = isStartupRequest ? "" : await this.tokenService.GetAccessToken();
        const authorizationHeader: string = 'Bearer ' + token;

        // return special config when pdf file gets requested
        const isPdfDownload = (path.startsWith("forms/") && path.endsWith("/export/pdf"))
            || (path.startsWith("v3/forms/") && path.endsWith("/pdf"));
        if(isPdfDownload){
            return {
                method: requestMethod,
                responseType: "arraybuffer",
                onDownloadProgress: (downloadEvent:any) => {
                    console.log("downloadEvent triggered", downloadEvent);
                },
                url: this.url,
                headers: {
                    'Authorization': authorizationHeader,
                    'Accept-Language': systemStore.userPreferences.general.userLocale ?? "de",
                    'Content-Type': "application/pdf"
                }
            }
        }

        if(requestMethod === "get")
        {
            // clean up auth header if startup config gets requested
            if(isStartupRequest){
                return {
                    method: requestMethod,
                    url: this.url,
                    headers: {'Accept-Language': systemStore.userPreferences.general.userLocale ?? "de"}
                };
            }

            return {
                method: requestMethod,
                url: this.url,
                headers: {
                    'Authorization': authorizationHeader,
                    'Accept-Language': systemStore.userPreferences.general.userLocale ?? "de"
                }
            }
        }

        let contentType = "application/json";

        const isFileUpload = path === this.pathHelper.plants.getPlantImportPath()
            || path === this.pathHelper.users.getUserImportPath(UsersType.anab)
            || path === this.pathHelper.users.getUserImportPath(UsersType.anv);
        if(requestMethod === "post" && isFileUpload){
            contentType = "multipart/form-data";
        }

        return {
            method: requestMethod,
            url: this.url,
            data: bodyContent,
            onUploadProgress: (uploadEvent:any) => {
                if (isFileUpload) console.log("file upload progress triggered", uploadEvent);
            },
            headers: {
                'Content-Type': contentType,
                'Authorization': authorizationHeader,
                'Accept-Language': systemStore.userPreferences.general.userLocale ?? "de"
            }
        }
    }

    // convert error to generalResponse if needed
    private convertErrorIfNeeded(resp:HttpClientResponse<any>):any {
        const error = resp[0];
        const data = resp[1];

        if(error) {
            const result = new GeneralResponse();

            // all validation erros are 400 errors
            result.statusCode = data.status !== 400
                ? data.status
                : data.data.status;
            result.statusDescription = data.status !== 400
                ? `${error.message}`
                : data.data.message;

            return result
        }

        return data;
    }
}
